import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { PricesUseCasesContext } from '@/scopes/prices/components/PricesUseCasesContext'
import { getPriceRangeSelector, isFirstMonthSelector } from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import {
  add,
  format,
  getDay,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  lastDayOfMonth,
  max,
  nextMonday,
  previousMonday,
  previousSunday,
  sub,
} from 'date-fns'
import * as React from 'react'
import { createContext, PropsWithChildren, useMemo, useState } from 'react'

export interface NavigationByWeeksContextType {
  isNavigationLeftEnabled?: boolean
  isNavigationRightEnabled?: boolean
  navigationDateFrom?: Date
  navigationDateTo?: Date
  navigateRight: () => void
  navigateLeft: () => void
  navigateToMonth: (startDate: Date) => void
}

export const NavigationByWeeksContext = createContext<NavigationByWeeksContextType>({
  isNavigationLeftEnabled: false,
  isNavigationRightEnabled: false,
  navigationDateFrom: undefined,
  navigationDateTo: undefined,
  navigateRight: () => {},
  navigateLeft: () => {},
  navigateToMonth: () => {},
})

enum ColumnsLengthLimits {
  DESKTOP = 7,
  TABLET = 5,
  MOBILE = 3,
}

export function NavigationByWeeksContextProvider({ children }: PropsWithChildren) {
  const {
    dateFrom,
    dateTo,
    options: months,
    minDatePrices,
    maxDatePrices,
    minPriceDate,
    selectedPriceDate,
  } = usePricesSelector(getPriceRangeSelector)
  const { isFirstMonth } = usePricesSelector(isFirstMonthSelector)
  const { selectRange } = React.useContext(PricesUseCasesContext)
  const { isTablet, isDesktopMd } = useBreakpoints()
  const columnsLengthLimit = useMemo(() => {
    if (isDesktopMd) {
      return ColumnsLengthLimits.DESKTOP
    }
    if (isTablet) {
      return ColumnsLengthLimits.TABLET
    }
    return ColumnsLengthLimits.MOBILE
  }, [isDesktopMd, isTablet])
  const [navigationDateFrom, setLocalNavigationDateFrom] = useState<Date>()
  const [navigationDateTo, setLocalNavigationDateTo] = useState<Date>()

  function getPreviousMonday(fromDate = new Date()) {
    if (columnsLengthLimit < ColumnsLengthLimits.DESKTOP) {
      return fromDate
    }
    return getDay(fromDate) === 1 ? fromDate : previousMonday(fromDate)
  }

  const isNavigationLeftEnabled = useMemo(() => {
    if (!navigationDateTo)
      return false

    if (!isFirstMonth(format(navigationDateTo, 'yyyy-MM-dd'))) {
      // we are in first month
      return true
    }

    const previousNavigationDateTo
      = columnsLengthLimit < ColumnsLengthLimits.DESKTOP
        ? sub(navigationDateTo, { days: columnsLengthLimit })
        : previousSunday(navigationDateTo)
    return (
      isAfter(previousNavigationDateTo, new Date(minDatePrices))
      || isEqual(previousNavigationDateTo, new Date(minDatePrices))
    )
  }, [navigationDateTo, minDatePrices, months])

  const isNavigationRightEnabled = useMemo(() => {
    if (!navigationDateFrom)
      return false

    const isItLastMonth = (date: Date) => {
      const monthValues = Object.values(months).flat()
      if (monthValues.length === 0) {
        return false
      }
      return monthValues[monthValues.length - 1].value === format(date, 'yyyy-MM')
    }

    if (!isItLastMonth(navigationDateFrom)) {
      // we are in last month
      return true
    }

    const nextNavigationDateFrom
      = columnsLengthLimit < ColumnsLengthLimits.DESKTOP
        ? add(navigationDateFrom, { days: columnsLengthLimit })
        : nextMonday(navigationDateFrom)
    return (
      isBefore(nextNavigationDateFrom, new Date(maxDatePrices))
      || isSameDay(nextNavigationDateFrom, new Date(maxDatePrices))
    )
  }, [navigationDateFrom, maxDatePrices, months])

  const navigateToMonth = (startDate: Date) => {
    const firstMonthMonday = getPreviousMonday(startDate)
    setLocalNavigationDateFrom(firstMonthMonday)
    setLocalNavigationDateTo(add(firstMonthMonday, { days: columnsLengthLimit - 1 }))
  }

  const navigateRight = () => {
    if (!navigationDateFrom || !navigationDateTo || !dateTo)
      return

    const newDateFrom = add(navigationDateFrom, { days: columnsLengthLimit })
    const newDateTo = add(navigationDateTo, { days: columnsLengthLimit })
    setLocalNavigationDateFrom(newDateFrom)
    setLocalNavigationDateTo(newDateTo)
    if (isAfter(newDateTo, new Date(dateTo)) || isSameDay(newDateTo, new Date(dateTo))) {
      const from = format(newDateTo, 'yyyy-MM-01')
      const to = format(lastDayOfMonth(newDateTo), 'yyyy-MM-dd')
      const month = format(newDateTo, 'yyyy-MM')
      selectRange({ dateFrom: from, dateTo: to, month })
    }
  }

  const navigateLeft = () => {
    if (!navigationDateFrom || !navigationDateTo || !dateFrom)
      return
    const newDateFrom = sub(navigationDateFrom, { days: columnsLengthLimit })
    const newDateTo = sub(navigationDateTo, { days: columnsLengthLimit })
    setLocalNavigationDateFrom(newDateFrom)
    setLocalNavigationDateTo(newDateTo)
    if (isBefore(newDateFrom, new Date(dateFrom)) || isSameDay(newDateFrom, new Date(dateFrom))) {
      const from = format(newDateFrom, 'yyyy-MM-01')
      const to = format(lastDayOfMonth(newDateFrom), 'yyyy-MM-dd')
      const month = format(newDateFrom, 'yyyy-MM')
      selectRange({ dateFrom: from, dateTo: to, month })
    }
  }

  React.useEffect(() => {
    if (dateFrom && !navigationDateFrom && minPriceDate) {
      const mondayOfFirstWeekWithPrices = getPreviousMonday(new Date(selectedPriceDate || minPriceDate))
      const from = max([new Date(dateFrom), mondayOfFirstWeekWithPrices])
      const to = add(from, { days: columnsLengthLimit - 1 })
      setLocalNavigationDateFrom(from)
      setLocalNavigationDateTo(to)
    }
  }, [dateFrom, minPriceDate, navigationDateFrom, columnsLengthLimit, selectedPriceDate])

  return (
    <NavigationByWeeksContext.Provider
      value={{
        navigationDateFrom,
        navigationDateTo,
        isNavigationLeftEnabled,
        isNavigationRightEnabled,
        navigateRight,
        navigateLeft,
        navigateToMonth,
      }}
    >
      {children}
    </NavigationByWeeksContext.Provider>
  )
}
